// CHAT-SWITCH-A: the channel's Files (Slack's Files tab). The header's "Files" opens a dialog of
// every file sent in the channel (`channels.files`), newest first, by kind and by who shared it;
// a row opens its message (the topic with the thread panel on it, `messageLink`) and downloads
// from its hover icon. "Load more" by cursor. Empty means empty: no sample rows.
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  EmptyState,
  NativeSelect,
  NativeSelectOption,
} from "@companyos/ui";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  DownloadIcon,
  FileIcon,
  FileImageIcon,
  FileSpreadsheetIcon,
  FileTextIcon,
  FilmIcon,
  type LucideIcon,
  PaperclipIcon,
} from "lucide-react";
import { useState } from "react";
import { LoadError, Loading } from "../screen-parts";
import { formatBytes } from "../work/files";
import { downloadChatFile, useChatBlobUrl } from "./chat-files";
import { channelLabel } from "./chat-model";
import { type Channel, chatKeys, membersQuery } from "./chat-queries";
import { messageLink } from "./chat-search";
import type { ChatContext } from "./use-chat";

type ChannelFile = Awaited<
  ReturnType<ChatContext["api"]["client"]["channels"]["files"]>
>["data"][number];
type Kind = ChannelFile["kind"];

export const FILE_KINDS: readonly { id: Kind | "all"; label: string }[] = [
  { id: "all", label: "All" },
  { id: "image", label: "Images" },
  { id: "pdf", label: "PDFs" },
  { id: "document", label: "Documents" },
  { id: "spreadsheet", label: "Spreadsheets" },
  { id: "media", label: "Media" },
  { id: "other", label: "Other" },
];

const KIND_ICON: Record<Kind, LucideIcon> = {
  image: FileImageIcon,
  pdf: FileTextIcon,
  document: FileTextIcon,
  spreadsheet: FileSpreadsheetIcon,
  media: FilmIcon,
  other: FileIcon,
};

export const channelFilesKey = (co: string, channel: string, kind: string, from: string) =>
  [...chatKeys.root(co), "files", channel, kind, from] as const;

/** The header's "Files": the button and its dialog. */
export function ChannelFilesButton({ chat, channel }: { chat: ChatContext; channel: Channel }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="ghost"
        size="xs"
        className="text-secondary"
        title="Files in this channel"
        onClick={() => setOpen(true)}
      >
        <PaperclipIcon data-icon="inline-start" />
        <span className="max-sm:sr-only">Files</span>
      </Button>
      {open ? (
        <ChannelFilesDialog chat={chat} channel={channel} onClose={() => setOpen(false)} />
      ) : null}
    </>
  );
}

export function ChannelFilesDialog({
  chat,
  channel,
  onClose,
}: {
  chat: ChatContext;
  channel: Channel;
  onClose: () => void;
}) {
  const [kind, setKind] = useState<Kind | "all">("all");
  const [from, setFrom] = useState("");
  const members = useQuery(membersQuery(chat.api, chat.co, channel.id));
  const files = useInfiniteQuery({
    queryKey: channelFilesKey(chat.co, channel.id, kind, from),
    queryFn: ({ pageParam }) =>
      chat.api.client.channels.files({
        params: { co: chat.co, id: channel.id },
        query: {
          limit: 30,
          ...(kind !== "all" ? { kind } : {}),
          ...(from ? { from } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
  });
  const rows = files.data?.pages.flatMap((p) => p.data) ?? [];
  const people = (members.data?.data ?? [])
    .map((m) => ({ id: m.membership_id, name: chat.members.get(m.membership_id)?.name }))
    .filter((m): m is { id: string; name: string } => Boolean(m.name))
    .sort((a, b) => a.name.localeCompare(b.name));
  const filtered = kind !== "all" || from !== "";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl" data-dialog="channel-files">
        <DialogHeader>
          <DialogTitle>Files · {channelLabel(channel, chat)}</DialogTitle>
          <DialogDescription>Every file shared here, newest first.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-1.5">
          <fieldset aria-label="Kind" className="m-0 flex flex-wrap gap-1 border-0 p-0">
            {FILE_KINDS.map((k) => (
              <Button
                key={k.id}
                size="xs"
                variant={kind === k.id ? "secondary" : "ghost"}
                aria-pressed={kind === k.id}
                onClick={() => setKind(k.id)}
              >
                {k.label}
              </Button>
            ))}
          </fieldset>
          <NativeSelect
            size="sm"
            aria-label="Shared by"
            className="ml-auto"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          >
            <NativeSelectOption value="">Anyone</NativeSelectOption>
            {people.map((p) => (
              <NativeSelectOption key={p.id} value={p.id}>
                {p.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </div>
        <div className="max-h-[60vh] min-h-32 overflow-y-auto">
          {files.isError ? (
            <LoadError what="Files" error={files.error} />
          ) : files.isPending ? (
            <Loading label="Loading files" />
          ) : !rows.length ? (
            <EmptyState
              variant="compact"
              icon={PaperclipIcon}
              title={filtered ? "No files match" : "No files yet"}
            />
          ) : (
            <ul aria-label="Files" className="m-0 flex list-none flex-col p-0">
              {rows.map((f) => (
                <FileRow key={f.id} chat={chat} channel={channel} file={f} onOpen={onClose} />
              ))}
            </ul>
          )}
          {files.hasNextPage ? (
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              disabled={files.isFetchingNextPage}
              onClick={() => void files.fetchNextPage()}
            >
              {files.isFetchingNextPage ? "Loading…" : "Load more"}
            </Button>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FileRow({
  chat,
  channel,
  file,
  onOpen,
}: {
  chat: ChatContext;
  channel: Channel;
  file: ChannelFile;
  onOpen: () => void;
}) {
  const Icon = KIND_ICON[file.kind];
  const thumb = useChatBlobUrl(chat.api, chat.co, file.has_thumb ? file.id : null, "thumb");
  const when = new Date(file.created_at).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  return (
    <li
      data-slot="channel-file"
      data-kind={file.kind}
      className="group/file relative flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-muted"
    >
      <span className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
        {thumb.url ? (
          <img src={thumb.url} alt="" className="size-full object-cover" />
        ) : (
          <Icon aria-hidden="true" className="size-5 text-muted-foreground" />
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        {file.message_id ? (
          <Link
            {...messageLink(chat, {
              channel_id: channel.id,
              topic_id: file.topic_id,
              message_id: file.message_id,
              parent_message_id: file.parent_message_id,
            })}
            onClick={onOpen}
            className="truncate text-sm font-medium text-foreground after:absolute after:inset-0 hover:underline focus-visible:outline-2 focus-visible:outline-ring"
          >
            {file.file_name}
          </Link>
        ) : (
          <span className="truncate text-sm font-medium">{file.file_name}</span>
        )}
        <span className="truncate text-xs text-muted-foreground">
          {file.uploader.name} · {when} · {formatBytes(file.bytes)}
        </span>
      </span>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={`Download ${file.file_name}`}
        title="Download"
        className={cn(
          "relative z-10 opacity-0 group-hover/file:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100",
        )}
        onClick={() => void downloadChatFile(chat.api, chat.co, file)}
      >
        <DownloadIcon />
      </Button>
    </li>
  );
}
